<template>
  <!-- ✅ 关键：只给本页面套 ConfigProvider，分页内置文案（跳至/页）才能切换 -->
  <a-config-provider :locale="antLocale">
    <div>
      <!-- 筛选项 -->
      <a-form layout="inline" class="mb-4">
        <a-form-item>
          <a-input
              v-model="query.nameOrId"
              :placeholder="t('phNameOrId')"
              allowClear
          />
        </a-form-item>

        <a-form-item>
          <a-select
              v-model="query.tagIdList"
              mode="multiple"
              allowClear
              style="width: 200px"
              :placeholder="t('phTag')"
          >
            <a-select-option
                v-for="tag in tagList"
                :key="tag.id"
                :value="tag.id"
            >
              {{ isFilmMerchant ? (tag.tagTitleEn || tag.tagTitle) : tag.tagTitle }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item>
          <a-select
              v-model="query.isRelease"
              :placeholder="t('phRelease')"
              allowClear
              style="width: 140px"
          >
            <a-select-option :value="1">{{ t('released') }}</a-select-option>
            <a-select-option :value="0">{{ t('unreleased') }}</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item>
          <a-button type="primary" @click="handleSearch">
            {{ t('search') }}
          </a-button>
        </a-form-item>
      </a-form>

      <!-- 表格 -->
      <a-table
          :columns="tableColumns"
          :data-source="data"
          :pagination="paginationConfig"
          :loading="loading"
          rowKey="id"
          @change="handleTableChange"
          :scroll="{ x: 2800 }"
      >
        <!-- 封面 -->
        <template slot="cover" slot-scope="text">
          <template v-if="text">
            <img :src="text" class="cover-img" />
          </template>
          <a-tag v-else>{{ t('noCover') }}</a-tag>
        </template>

        <!-- 上线状态 -->
        <template slot="isRelease" slot-scope="text">
          <a-tag v-if="+text === 1" color="green">{{ t('released') }}</a-tag>
          <a-tag v-else color="red">{{ t('unreleased') }}</a-tag>
        </template>

        <!-- 上线时间 -->
        <template slot="onlineTime" slot-scope="text">
          <span>{{ text || '-' }}</span>
        </template>

        <!-- 解锁（广告/付费） -->
        <template slot="unlock" slot-scope="text, record">
          {{ toInt(record.adUnlockCount) }} / {{ toInt(record.payUnlockCount) }}
        </template>

        <!-- 时长通用渲染（秒 或 'HH:mm:ss'） -->
        <template slot="duration" slot-scope="text">
          {{ formatDuration(text) }}
        </template>

        <!-- 百分比通用渲染（0~1 或 0~100） -->
        <template slot="percent" slot-scope="text">
          {{ formatPercent(text) }}
        </template>

        <!-- 金额通用渲染 -->
        <template slot="money" slot-scope="text">
          {{ formatMoney(text) }}
        </template>

        <!-- 主要付费节点 -->
        <template slot="nodeList" slot-scope="text">
          {{ formatNodes(text) }}
        </template>

        <!-- 操作 -->
        <template slot="action" slot-scope="text, record">
          <a @click="viewDetail(record)">{{ t('viewEpisodeStats') }}</a>
        </template>
      </a-table>
    </div>
  </a-config-provider>
</template>

<script>
import request from '@/utils/request'
import { mapGetters } from 'vuex'

// ✅ 文案包：片商英文；非片商中文
const TEXT = {
  zh: {
    // filter
    phNameOrId: '短剧编号(ID)/名称',
    phTag: '选择短剧分类',
    phRelease: '上线状态',
    released: '已上线',
    unreleased: '未上线',
    search: '查询',

    // table
    noCover: '无封面',
    viewEpisodeStats: '查看单集统计',

    // pagination
    total: total => `共 ${total} 条`,

    // msg
    loadFail: '数据加载失败',
    tagFail: '分类加载失败'
  },
  en: {
    // filter
    phNameOrId: 'Drama ID / Name',
    phTag: 'Select Categories',
    phRelease: 'Release Status',
    released: 'Released',
    unreleased: 'Unreleased',
    search: 'Search',

    // table
    noCover: 'No Cover',
    viewEpisodeStats: 'Episode Analytics',

    // pagination
    total: total => `Total ${total}`,

    // msg
    loadFail: 'Failed to load data',
    tagFail: 'Failed to load categories'
  }
}

// ✅ 关键：Ant Design Vue Pagination 内置文案（跳至 / 页）
// 只配 Pagination 这块即可，本页面通过 a-config-provider 生效
const ANT_LOCALE = {
  zh: {
    Pagination: {
      jump_to: '跳至',
      page: '页'
    }
  },
  en: {
    Pagination: {
      jump_to: 'Jump to',
      page: 'Page'
    }
  }
}

// ✅ 列标题翻译（按 dataIndex）
const COL_TITLE = {
  en: {
    coverUrl: 'Cover',
    id: 'Drama ID',
    videoName: 'Title',
    tagIds: 'Type',
    videoBelong: 'Ownership',
    isRelease: 'Status',
    onlineTime: 'Release Time',
    totalNum: 'Total Episodes',

    unlock: 'Unlocks (Ad / Paid)',
    totalViewCount: 'Total Views',
    totalValidWatchSeconds: 'Total Valid Watch Time',
    avgWatchSeconds: 'Average Watch Time',
    finishRate: 'Completion Rate (Series)',
    favoriteCount: 'Total Favorites',
    favoriteRate: 'Favorites Rate',
    danmakuCount: 'Comments (Danmu)',
    danmakuRate: 'Comment Rate (Danmu)',

    shareCount: 'Total Shares',
    shareRate: 'Share Rate',
    viewUserCount: 'Total Viewers',
    viewFinishUserCount: 'Finished Viewers',
    totalRevenue: 'Total Revenue',
    totalPayRevenue: 'Paid Revenue',
    filmRevenue: 'Film Partner Revenue',
    totalPayUserCount: 'Paying Users',
    totalAdRevenue: 'Ad Revenue',
    payRate: 'Pay Rate',

    arppu: 'ARPPU',
    avgDropRate: 'Avg Drop Rate',
    mainPayNodes: 'Main Pay Nodes',

    viewRevenue: 'View Revenue',
    adRevenue: 'Ad Revenue',
    viewAndAdRevenue: 'Total Earnings (RM)',

    action: 'Action'
  }
}

export default {
  name: 'VideoStatisticsList',
  data() {
    return {
      // 金额展示配置：unit 可选 'yuan' | 'cent'
      moneyUnit: 'yuan',
      currencySymbol: 'RM',

      query: {
        nameOrId: '',
        type: '',
        isRelease: undefined,
        tagIdList: []
      },
      data: [],
      loading: false,
      tagList: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true
      },

      // 列定义（中文原样保留，非片商不变）
      columns: [
        { title: '短剧封面', dataIndex: 'coverUrl', width: 100, align: 'center', scopedSlots: { customRender: 'cover' } },
        { title: '短剧编号（ID）', dataIndex: 'id', width: 140, align: 'center' },
        { title: '短剧名称', dataIndex: 'videoName', align: 'left', width: 240 },
        { title: '类型', dataIndex: 'tagIds', align: 'center', width: 140 },
        { title: '归属', dataIndex: 'videoBelong', align: 'center', width: 120 },
        { title: '上线状态', dataIndex: 'isRelease', width: 110, align: 'center', scopedSlots: { customRender: 'isRelease' } },
        { title: '上线时间', dataIndex: 'onlineTime', width: 110, align: 'center', scopedSlots: { customRender: 'onlineTime' } },
        { title: '总集数', dataIndex: 'totalNum', width: 90, align: 'center' },

        {
          title: '剧集解锁总数（广告解锁数/付费解锁数）',
          dataIndex: 'unlock',
          width: 260,
          align: 'center',
          scopedSlots: { customRender: 'unlock' }
        },
        { title: '总观看次数', dataIndex: 'totalViewCount', width: 120, align: 'center' },
        {
          title: '总有效观看时长',
          dataIndex: 'totalValidWatchSeconds',
          width: 150,
          align: 'center',
          scopedSlots: { customRender: 'duration' }
        },
        {
          title: '人均观看时长',
          dataIndex: 'avgWatchSeconds',
          width: 150,
          align: 'center',
          scopedSlots: { customRender: 'duration' }
        },
        {
          title: '完播率（整剧）',
          dataIndex: 'finishRate',
          width: 130,
          align: 'center',
          scopedSlots: { customRender: 'percent' }
        },
        { title: '总点收藏', dataIndex: 'favoriteCount', width: 110, align: 'center' },
        {
          title: '收藏率',
          dataIndex: 'favoriteRate',
          width: 110,
          align: 'center',
          scopedSlots: { customRender: 'percent' }
        },
        { title: '评论数（弹幕）', dataIndex: 'danmakuCount', width: 140, align: 'center' },
        {
          title: '评论率（弹幕）',
          dataIndex: 'danmakuRate',
          width: 140,
          align: 'center',
          scopedSlots: { customRender: 'percent' }
        },

        { title: '总分享数', dataIndex: 'shareCount', width: 110, align: 'center' },
        { title: '分享率', dataIndex: 'shareRate', width: 100, align: 'center', scopedSlots: { customRender: 'percent' } },
        { title: '观看用户数', dataIndex: 'viewUserCount', width: 120, align: 'center' },
        { title: '观看完成用户数', dataIndex: 'viewFinishUserCount', width: 140, align: 'center' },
        { title: '总收入', dataIndex: 'totalRevenue', width: 120, align: 'center', scopedSlots: { customRender: 'money' } },
        {
          title: '总付费收入',
          dataIndex: 'totalPayRevenue',
          width: 130,
          align: 'center',
          scopedSlots: { customRender: 'money' }
        },
        {
          title: '片商收益',
          dataIndex: 'filmRevenue',
          width: 130,
          align: 'center',
          scopedSlots: { customRender: 'money' }
        },
        { title: '总付费人数', dataIndex: 'totalPayUserCount', width: 120, align: 'center' },
        {
          title: '总广告收入',
          dataIndex: 'totalAdRevenue',
          width: 130,
          align: 'center',
          scopedSlots: { customRender: 'money' }
        },
        { title: '付费率', dataIndex: 'payRate', width: 100, align: 'center', scopedSlots: { customRender: 'percent' } },

        { title: 'ARPPU', dataIndex: 'arppu', width: 110, align: 'center', scopedSlots: { customRender: 'money' } },
        {
          title: '集均流失率',
          dataIndex: 'avgDropRate',
          width: 120,
          align: 'center',
          scopedSlots: { customRender: 'percent' }
        },
        {
          title: '主要付费节点',
          dataIndex: 'mainPayNodes',
          width: 160,
          align: 'center',
          scopedSlots: { customRender: 'nodeList' }
        },

        // 片商专属三列
        {
          title: '观看收入',
          dataIndex: 'viewRevenue',
          width: 120,
          align: 'center',
          scopedSlots: { customRender: 'money' }
        },
        { title: '广告收入', dataIndex: 'adRevenue', width: 120, align: 'center', scopedSlots: { customRender: 'money' } },
        {
          title: '总收入',
          dataIndex: 'viewAndAdRevenue',
          width: 120,
          align: 'center',
          scopedSlots: { customRender: 'money' }
        },

        { title: '操作', dataIndex: 'action', fixed: 'right', width: 140, scopedSlots: { customRender: 'action' } }
      ]
    }
  },

  computed: {
    ...mapGetters(['roles']),

    // ✅ 片商角色：roles 包含 film_user
    isFilmMerchant() {
      const rs = this.roles || []
      return rs.includes('film_user')
    },

    // ✅ 片商英文、非片商中文
    lang() {
      return this.isFilmMerchant ? 'en' : 'zh'
    },

    // ✅ 只影响本页的 Ant Design Vue 内置文案（Pagination: 跳至/页）
    antLocale() {
      return ANT_LOCALE[this.lang] || ANT_LOCALE.zh
    },

    // ✅ 分页 showTotal 按语言切换（左侧“共xx条”）
    paginationConfig() {
      return {
        ...this.pagination,
        showTotal: total => (TEXT[this.lang] || TEXT.zh).total(total)
      }
    },

    tableColumns() {
      // 1) 仅片商：翻译列标题
      const translated = this.columns.map(col => {
        if (this.lang !== 'en') return col
        const map = COL_TITLE.en || {}
        const newTitle = map[col.dataIndex] || col.title
        return { ...col, title: newTitle }
      })

      // 2) 按角色隐藏列
      const hiddenForFilmMerchant = [
        'videoBelong',
        'totalRevenue',
        'totalPayRevenue',
        'filmRevenue',
        'totalAdRevenue',
        'arppu',
        'payRate',
        'mainPayNodes',
        'totalPayUserCount',
        'unlock',
        'totalValidWatchSeconds',
        'avgWatchSeconds',
        'finishRate',
        'viewFinishUserCount',
        'avgDropRate',
        'favoriteRate',
        'danmakuCount',
        'danmakuRate',
        'shareRate',
        // ✅ 注意：不要把 viewRevenue/adRevenue 放进来，否则片商三列会被隐藏
        // 'adRevenue',
        // 'viewRevenue'
      ]

      const hiddenForNonFilm = [
        'viewAndAdRevenue',
        'viewRevenue',
        'adRevenue'
      ]

      if (this.isFilmMerchant) {
        return translated.filter(col => !hiddenForFilmMerchant.includes(col.dataIndex))
      }
      return translated.filter(col => !hiddenForNonFilm.includes(col.dataIndex))
    }
  },

  methods: {
    t(key) {
      const pack = TEXT[this.lang] || TEXT.zh
      return pack[key] || key
    },

    async fetchData() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.current,
          pageSize: this.pagination.pageSize
        }

        if (this.query.type) params.type = this.query.type
        if (this.query.isRelease === 0 || this.query.isRelease === 1) {
          params.isRelease = this.query.isRelease
        }
        if (this.query.tagIdList && this.query.tagIdList.length > 0) {
          params.tagIdList = this.query.tagIdList
        }

        const kw = (this.query.nameOrId || '').trim()
        if (kw) {
          if (/^\d+$/.test(kw)) params.id = kw
          else params.videoName = kw
        }

        const res = await request.get('/data/aggregation/video/page', { params })
        const { records, total } = res.data || {}
        this.data = records || []
        this.pagination.total = total || 0
      } catch (e) {
        this.$message.error(this.t('loadFail'))
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.current = 1
      this.fetchData()
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.fetchData()
    },

    async loadTags() {
      try {
        const res = await request.get('/video/video-tags/page', {
          params: { pageNum: 1, pageSize: 100 }
        })
        this.tagList = (res.data && res.data.records) ? res.data.records : []
      } catch (e) {
        this.$message.error(this.t('tagFail'))
      }
    },

    viewDetail(record) {
      this.$router.push({
        name: 'GatherDetail',
        query: { title: record.videoName, videoId: record.id }
      })
    },

    // ===== 工具函数 =====
    formatDuration(val) {
      if (val === null || val === undefined || val === '') return '-'
      if (typeof val === 'string' && val.indexOf(':') > -1) return val
      const sec = Math.max(0, Number(val) || 0)
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = Math.floor(sec % 60)
      const pad = n => String(n).padStart(2, '0')
      return `${pad(h)}:${pad(m)}:${pad(s)}`
    },

    // 支持 0~1 或 0~100
    formatPercent(val) {
      if (val === null || val === undefined || val === '') return '-'
      const num = Number(val)
      if (!Number.isFinite(num)) return '-'
      const p = num <= 1 ? (num * 100) : num
      return `${p.toFixed(0)}%`
    },

    toInt(v) {
      const n = Number(v)
      return Number.isFinite(n) ? Math.floor(n) : 0
    },

    // 金额格式化：moneyUnit = 'yuan'|'cent'
    formatMoney(val) {
      if (val === null || val === undefined || val === '') {
        return `${this.currencySymbol} 0.00`
      }

      // 避免后端已经带 RM
      const str = String(val).trim()
      if (/^RM\s*/i.test(str)) return str

      let num = Number(val)
      if (!Number.isFinite(num)) return `${this.currencySymbol} 0.00`
      if (this.moneyUnit === 'cent') num = num / 100
      return `${this.currencySymbol} ${num.toFixed(2)}`
    },

    formatNodes(v) {
      if (v === null || v === undefined || v === '') return '-'
      if (Array.isArray(v)) return v.join(', ')
      if (typeof v === 'string') {
        return (
            v
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .join(', ') || '-'
        )
      }
      return String(v)
    }
  },

  mounted() {
    this.loadTags()
    this.fetchData()
  },

  watch: {
    // 你原来是输入就触发 fetchData，这里保持一致
    'query.nameOrId': function () {
      this.fetchData()
    },
    'query.tagIdList': function () {
      this.fetchData()
    },
    'query.isRelease': function () {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.mb-4 {
  margin-bottom: 16px;
}

.cover-img {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
